// Map logs of an external match: per map, where its data comes from (live log / uploaded files) and
// the upload of a map's CS2 log file (services/ext/upload.js). One file per upload; a map that
// crashed and was hosted again = several files, joined in time order by the server.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { card, h2, sub, btn, pill, RGB, Icon, ICONS, ConfirmButton, errMsg } from './extUi';

const MAX_BYTES = 32 * 1024 * 1024;
const hhmm = (ts) => (ts ? String(ts).slice(11, 16) : '?');

function MapRow({ row, base, teams, onChanged }) {
  const { t } = useTranslation();
  const input = useRef(null);
  const [busy, setBusy] = useState(null);     // upload progress 0–100
  const [result, setResult] = useState(null); // { map, warnings }
  const [error, setError] = useState(null);   // { message, players }
  const [pickWinner, setPickWinner] = useState(false); // "Mark as forfeited" → which team gets the map
  const label = t('ext.mapN', 'Map {{n}}', { n: row.map_index + 1 });
  const replacesLive = row.source === 'live' && !row.files.length;
  // forfeit: PUT/DELETE …/matches/:mid/maps/:i/forfeit (the match is re-parsed, the veto order is kept)
  const forfeitUrl = `${base.replace(/\/map-logs$/, '/maps')}/${row.map_index}/forfeit`;
  const teamName = (k) => (teams && teams[k] && teams[k].name) || (k === 'team1' ? 'Team 1' : 'Team 2');
  const canForfeit = !row.forfeit && !(row.status === 'finished' && row.source !== 'forfeit') && !row.files.length;
  const setForfeit = async (winner) => {
    setError(null); setResult(null); setPickWinner(false);
    try {
      if (winner) await axios.put(forfeitUrl, { winner }); else await axios.delete(forfeitUrl);
      onChanged();
    } catch (e) { setError({ message: errMsg(e, t('ext.actionFailed', 'Action failed')) }); }
  };

  const pick = () => { setError(null); setResult(null); if (input.current) { input.current.value = ''; input.current.click(); } };
  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) { setError({ message: t('ext.mapLogs.tooLarge', 'The file is larger than 32 MB - upload one map per file.') }); return; }
    setBusy(0); setError(null);
    try {
      const text = await file.text();
      const r = await axios.post(`${base}/${row.map_index}`, text, {
        headers: { 'Content-Type': 'text/plain' }, params: { filename: file.name }, timeout: 300000,
        onUploadProgress: (e) => setBusy(e.total ? Math.round((e.loaded / e.total) * 100) : null),
      });
      setResult(r.data);
      onChanged();
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.error;
      setError({ message: errMsg(e, t('ext.mapLogs.failed', 'Upload failed')), players: d && d.players });
    } finally { setBusy(null); }
  };
  const remove = async (fileNo) => {
    setError(null); setResult(null);
    try { await axios.delete(fileNo != null ? `${base}/${row.map_index}/${fileNo}` : `${base}/${row.map_index}`); onChanged(); }
    catch (e) { setError({ message: errMsg(e, t('ext.actionFailed', 'Action failed')) }); }
  };

  const badge = row.forfeit
    ? <span style={pill(RGB.amber)}>{t('ext.mapLogs.forfeitBadge', 'Forfeit · {{team}} gets the map', { team: teamName(row.forfeit.winner) })}</span>
    : row.source === 'upload'
    ? <span style={pill(RGB.violet)}>{row.files.length === 1 ? t('ext.mapLogs.uploadedOne', 'Uploaded') : t('ext.mapLogs.uploaded', 'Uploaded ({{n}} files)', { n: row.files.length })}</span>
    : row.source === 'live' ? <span style={pill(RGB.green)}>{t('ext.mapLogs.live', 'Live log')}</span>
      : <span style={pill(RGB.gray)}>{t('ext.mapLogs.none', 'No data')}</span>;

  return (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '0.6rem 0.75rem', marginBottom: '0.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.86rem' }}>{label}</span>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', textTransform: 'capitalize' }}>{row.map ? row.map.replace(/^de_/, '') : '—'}</span>
        {row.score && !row.forfeit && <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.8rem', color: 'var(--text-primary)' }}>{row.score.team1} – {row.score.team2}</span>}
        {badge}
        <span style={{ flex: 1 }} />
        {row.source !== 'none' && row.source !== 'forfeit' && (
          <a style={{ ...btn(RGB.gray), textDecoration: 'none' }} href={`${base}/${row.map_index}/download?token=${encodeURIComponent(localStorage.getItem('token') || '')}`}
            title={t('ext.mapLogs.downloadHint', 'The lines this map\'s stats are built from')}><Icon d={ICONS.download} /> {t('ext.mapLogs.download', 'Download log')}</a>
        )}
        {canForfeit && !pickWinner && (
          <button type="button" style={btn(RGB.amber)} onClick={() => { setError(null); setPickWinner(true); }}>{t('ext.mapLogs.markForfeit', 'Mark as forfeited')}</button>
        )}
        {row.forfeit && (
          <ConfirmButton label={t('ext.mapLogs.removeForfeit', 'Remove forfeit')} icon={ICONS.refresh} color={RGB.amber}
            warning={t('ext.mapLogs.removeForfeitWarning', 'The map is no longer counted for {{team}}. The match is rebuilt from its logs.', { team: teamName(row.forfeit.winner) })} onConfirm={() => setForfeit(null)} />
        )}
        <input ref={input} type="file" accept=".log,.txt,text/plain" style={{ display: 'none' }} onChange={e => upload(e.target.files && e.target.files[0])} />
        {row.forfeit ? null : busy != null
          ? <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)' }}>{t('ext.mapLogs.uploading', 'Uploading… {{pct}}%', { pct: busy })}</span>
          : replacesLive
            ? <ConfirmButton label={t('ext.mapLogs.upload', 'Upload log')} icon={ICONS.upload} color={RGB.blue}
                warning={t('ext.mapLogs.replaceWarning', 'Replaces this map\'s data with uploaded files. The live log is kept and comes back if you remove the files.')} onConfirm={pick} />
            : <button type="button" style={btn(RGB.blue)} onClick={pick}><Icon d={ICONS.upload} /> {row.files.length ? t('ext.mapLogs.addFile', 'Add another file') : t('ext.mapLogs.upload', 'Upload log')}</button>}
      </div>

      {pickWinner && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap', marginTop: '0.5rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          <span>{t('ext.mapLogs.forfeitWho', 'Which team gets {{map}}? It counts as a map won; the other maps keep the veto order.', { map: row.map ? row.map.replace(/^de_/, '') : label })}</span>
          <button type="button" style={btn(RGB.green)} onClick={() => setForfeit('team1')}>{teamName('team1')}</button>
          <button type="button" style={btn(RGB.green)} onClick={() => setForfeit('team2')}>{teamName('team2')}</button>
          <button type="button" style={btn(RGB.gray)} onClick={() => setPickWinner(false)}>{t('common.cancel', 'Cancel')}</button>
        </div>
      )}

      {busy != null && (
        <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.06)', marginTop: '0.5rem', overflow: 'hidden' }}>
          <div style={{ width: `${busy || 5}%`, height: '100%', background: 'var(--accent-blue)', transition: 'width 0.2s' }} />
        </div>
      )}

      {row.files.length > 0 && (
        <div style={{ marginTop: '0.45rem' }}>
          {row.files.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.74rem', color: 'var(--text-secondary)', padding: '0.2rem 0' }}>
              <span style={{ fontFamily: 'var(--font-mono, monospace)', color: 'var(--text-primary)', wordBreak: 'break-all' }}>{f.filename || t('ext.mapLogs.fileN', 'File {{n}}', { n: f.id })}</span>
              <span>{t('ext.mapLogs.fileInfo', '{{lines}} lines · {{from}} → {{to}} · by {{who}}', { lines: f.lines.toLocaleString(), from: hhmm(f.first_at), to: hhmm(f.last_at), who: f.uploaded_by || '?' })}</span>
              <span style={{ flex: 1 }} />
              <ConfirmButton label={t('ext.mapLogs.remove', 'Remove')} icon={ICONS.trash} warning={t('ext.mapLogs.removeWarning', 'Removes this file from the map (the lines stay stored). The map is rebuilt.')} onConfirm={() => remove(f.id)} />
            </div>
          ))}
          {row.files.length > 1 && (
            <ConfirmButton label={t('ext.mapLogs.removeAll', 'Back to the live log')} icon={ICONS.refresh} color={RGB.amber}
              warning={t('ext.mapLogs.removeAllWarning', 'Removes all uploaded files of this map; it uses its live log again.')} onConfirm={() => remove(null)} />
          )}
        </div>
      )}

      {result && (
        <div style={{ marginTop: '0.45rem', fontSize: '0.76rem', color: 'var(--accent-green)' }}>
          {t('ext.mapLogs.done', 'Uploaded - {{map}} {{s1}}–{{s2}}, {{rounds}} rounds, {{players}} players', {
            map: (result.map.map || '?').replace(/^de_/, ''), s1: result.map.score ? result.map.score.team1 : 0, s2: result.map.score ? result.map.score.team2 : 0, rounds: result.map.rounds, players: result.map.players,
          })}
          {(result.warnings || []).map(w => <div key={w.code} style={{ color: 'var(--accent-amber)' }}>⚠ {w.message}</div>)}
        </div>
      )}
      {error && (
        <div style={{ marginTop: '0.45rem', fontSize: '0.76rem', color: 'var(--accent-red)' }}>
          {error.message}
          {Array.isArray(error.players) && error.players.length > 0 && (
            <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0, color: 'var(--text-secondary)' }}>
              {error.players.map(p => <li key={p.steam_id + p.name} style={{ fontFamily: 'var(--font-mono, monospace)' }}>{p.name} · {p.steam_id}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function ExtMapLogs({ tournamentId, matchId, teams, onChanged }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const base = `/api/v1/ext/tournaments/${tournamentId}/matches/${matchId}/map-logs`;
  const load = useCallback(async () => {
    try { const r = await axios.get(base); setRows(r.data.data || []); } catch (_) { }
  }, [base]);
  useEffect(() => { load(); }, [load]);
  if (!rows) return null;
  return (
    <div style={card}>
      <h2 style={h2}>{t('ext.mapLogs.title', 'Map logs')}</h2>
      <p style={sub}>{t('ext.mapLogs.hint', 'Live log missing or broken? Download the map\'s .log file from the game server and upload it here - it replaces that map\'s data. Server crashed during the map? Upload each log file of the map; they are joined in time order.')}</p>
      {rows.map(row => <MapRow key={row.map_index} row={row} base={base} teams={teams} onChanged={() => { load(); if (onChanged) onChanged(); }} />)}
    </div>
  );
}
